/**
 * Reset Password V2 Route (Tailwind)
 *
 * Verarbeitet Supabase Password Reset Links und ermöglicht das Setzen eines neuen Passworts.
 * URL Format: /reset-password#access_token=...&type=recovery
 *
 * WCAG 2.1 Level AA + BFSG konform
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { type SupabaseClient } from '@supabase/supabase-js';
import { toast } from 'sonner';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { getSupabaseClient } from '@/lib/supabase-client';
import { Key, CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/v2/ui/Card';
import { Button } from '@/components/v2/ui/Button';
import { Input } from '@/components/v2/ui/Input';
import { Alert, AlertDescription } from '@/components/v2/ui/Alert';
import { Loading } from '@/components/v2/ui/Loading';

// Logout aus BEIDEN Auth-Systemen (Flask + Supabase)
async function logoutCompletely(supabase: SupabaseClient | null) {
  // 1. Flask-Backend Session beenden
  try {
    await axios.post('/api/v2/auth/logout', {}, {
      withCredentials: true,
      headers: { 'Content-Type': 'application/json' }
    });
  } catch (e) {
    console.log('Flask logout (may already be logged out):', e);
  }

  // 2. Supabase Session beenden
  if (supabase) {
    try {
      await supabase.auth.signOut();
    } catch (e) {
      console.log('Supabase logout:', e);
    }
  }

  // 3. LocalStorage komplett clearen (Supabase tokens etc.)
  localStorage.clear();

  // 4. Session Storage clearen
  sessionStorage.clear();
}

export function ResetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [supabase, setSupabase] = useState<SupabaseClient | null>(null);

  useEffect(() => {
    // Initialisiere Supabase Client und parse URL-Fragment
    const initSupabase = async () => {
      try {
        const client = await getSupabaseClient();
        setSupabase(client);

        // Parse das URL-Fragment und setze die Session
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        const type = hashParams.get('type');

        if (type === 'recovery' && accessToken && refreshToken) {
          // Setze die Session mit dem Recovery Token
          const { error } = await client.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });

          if (error) {
            console.error('Session error:', error);
            setError(t('auth.invalidResetLink'));
          } else {
            setSessionReady(true);
          }
        } else if (!accessToken) {
          setError(t('auth.noValidResetLink'));
        }
      } catch (err) {
        console.error('Supabase init error:', err);
        setError(t('errors.configLoad'));
      } finally {
        setInitializing(false);
      }
    };

    initSupabase();
  }, [t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!supabase) {
      setError('Supabase Client nicht initialisiert.');
      return;
    }

    // Validierung
    if (password.length < 8) {
      setError('Das Passwort muss mindestens 8 Zeichen lang sein.');
      return;
    }

    if (password !== confirmPassword) {
      setError(t('auth.passwordsDoNotMatch'));
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({ password });

      if (error) {
        throw error;
      }

      // WICHTIG: Komplett ausloggen aus BEIDEN Auth-Systemen!
      await logoutCompletely(supabase);

      setSuccess(true);
      toast.success(t('toast.success.passwordChanged'));

      // Nach 2 Sekunden zum Login weiterleiten
      setTimeout(() => {
        window.location.href = '/v2/login';
      }, 2000);

    } catch (err: unknown) {
      console.error('Password update error:', err);
      await logoutCompletely(supabase);
      setError(err instanceof Error ? err.message : t('errors.saving'));
    } finally {
      setLoading(false);
    }
  };

  // Initializing State
  if (initializing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 dark:bg-neutral-950 px-4">
        <Loading size="lg" label={t('auth.checkingLink')} centered />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50 dark:bg-neutral-950 px-4 py-12">
      <div className="w-full max-w-md">
        <Card className="shadow-xl border-neutral-200 dark:border-neutral-800">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900">
              <Key className="h-7 w-7 text-primary-600 dark:text-primary-400" />
            </div>
            <CardTitle className="text-2xl">{t('auth.changePassword')}</CardTitle>
            <CardDescription>
              {t('auth.newPassword')}
            </CardDescription>
          </CardHeader>

          <CardContent className="pt-6">
            {/* Success State */}
            {success && (
              <Alert variant="success" icon={<CheckCircle className="h-5 w-5" />}>
                <AlertDescription>
                  <strong>{t('auth.passwordChanged')}</strong>
                  <p className="mt-1">{t('auth.backToLogin')}</p>
                </AlertDescription>
              </Alert>
            )}

            {/* Error State (invalid link) */}
            {error && !sessionReady && !success && (
              <div className="space-y-4">
                <Alert variant="error" icon={<AlertCircle className="h-5 w-5" />}>
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate('/v2/login')}
                  iconLeft={<ArrowLeft className="h-4 w-4" />}
                >
                  {t('auth.backToLogin')}
                </Button>
              </div>
            )}

            {/* Form State */}
            {sessionReady && !success && (
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Inline Error */}
                {error && (
                  <Alert variant="error" icon={<AlertCircle className="h-5 w-5" />}>
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <Input
                  label={t('auth.newPassword')}
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mindestens 8 Zeichen"
                  required
                  disabled={loading}
                  helperText="Mindestens 8 Zeichen"
                  iconLeft={<Key className="h-4 w-4" />}
                />

                <Input
                  label={t('auth.confirmPassword')}
                  type="password"
                  id="confirm-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Passwort wiederholen"
                  required
                  disabled={loading}
                  iconLeft={<Key className="h-4 w-4" />}
                />

                <Button
                  type="submit"
                  loading={loading}
                  disabled={!sessionReady || !supabase}
                  className="w-full"
                >
                  {t('auth.changePassword')}
                </Button>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => navigate('/v2/login')}
                    className="text-sm text-primary-600 hover:text-primary-500 dark:text-primary-400"
                  >
                    {t('auth.backToLogin')}
                  </button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default ResetPassword;
